// Request/response contracts, one per endpoint in API §2.
import { z } from 'zod';
import { pagesJsonSchema } from './edition.js';
import {
  contributionSchema,
  cycleSchema,
  editionSummarySchema,
  familySummarySchema,
  isoDate,
  memberSchema,
  notificationPreferenceSchema,
  notificationSchema,
  occasionSchema,
  paginationSchema,
  petSchema,
  photoSchema,
  promptSchema,
  reactionSchema,
  userSchema,
  uuid,
  yearMonth,
} from './entities.js';
import {
  LIMITS,
  contributionStatus,
  type cycleStatus,
  departmentStatus,
  departmentType,
  devicePlatform,
  editionStatus,
  membershipRole,
  membershipStatus,
  notificationType,
  occasionRecurrence,
  occasionSubjectType,
  occasionType,
  photoMimeType,
  reactionTargetType,
  reactionType,
  textSizePref,
} from './enums.js';

/** Uniform error envelope for every non-2xx response (API §6). */
export const errorEnvelopeSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
  }),
});
export type ErrorEnvelope = z.infer<typeof errorEnvelopeSchema>;

// ---------- Users (API §2.2) ----------
export const updateUserRequest = z.object({
  name: z.string().min(1).max(80).optional(),
  avatar_url: z.string().url().nullable().optional(),
  birthday: isoDate.nullable().optional(),
  city: z.string().max(80).nullable().optional(),
  text_size_pref: textSizePref.optional(),
});
export const deleteUserRequest = z.object({ confirmation: z.literal('DELETE') });

// ---------- Families (API §2.3) ----------
export const createFamilyRequest = z.object({
  name: z.string().min(1).max(80),
  timezone: z.string().min(1).default('UTC'),
});
export const createFamilyResponse = z.object({
  family: familySummarySchema,
  membership: z.object({ role: membershipRole, status: membershipStatus }),
  cycle: cycleSchema,
});
export const listFamiliesResponse = z.object({ families: z.array(familySummarySchema) });
export const familyDetailResponse = z.object({
  id: uuid,
  name: z.string(),
  timezone: z.string(),
  dietary_note: z.string().nullable(),
  created_by: uuid,
  members: z.array(memberSchema),
  pets: z.array(petSchema),
  current_cycle: cycleSchema.nullable(),
  my_membership: z.object({
    role: membershipRole,
    status: membershipStatus,
    archive_access_from: isoDate.nullable(),
  }),
});
export const updateFamilyRequest = z.object({
  name: z.string().min(1).max(80).optional(),
  timezone: z.string().optional(),
  dietary_note: z.string().max(280).nullable().optional(),
});
export const inviteResponse = z.object({
  invite_link: z.string(),
  token: z.string(),
  expires_at: z.string(),
});
export const listInvitesResponse = z.object({
  invites: z.array(
    z.object({
      id: uuid,
      created_at: z.string(),
      expires_at: z.string(),
      revoked_at: z.string().nullable(),
      use_count: z.number().int(),
    }),
  ),
});

// ---------- Membership (API §2.4) ----------
export const joinFamilyRequest = z.object({ invite_token: z.string().min(10) });
export const joinFamilyResponse = z.object({
  membership: z.object({ role: membershipRole, status: membershipStatus }),
  family: familySummarySchema,
});
export const inviteInfoResponse = z.object({
  valid: z.boolean(),
  reason: z.enum(['ok', 'expired', 'revoked', 'invalid']),
  family_name: z.string().nullable(),
  member_count: z.number().int().nullable(),
});
export const updateRoleRequest = z.object({ role: membershipRole });
export const grantArchiveRequest = z.object({ access_from: isoDate });
export const listMembersResponse = z.object({ members: z.array(memberSchema) });

// ---------- Pets (API §2.5) ----------
export const createPetRequest = z.object({
  name: z.string().min(1).max(60),
  species: z.string().min(1).max(40),
  avatar_url: z.string().url().nullable().optional(),
  birthday: isoDate.nullable().optional(),
});
export const updatePetRequest = createPetRequest.partial();
export const listPetsResponse = z.object({ pets: z.array(petSchema) });

// ---------- Cycles & prompts (API §2.6) ----------
export const currentCycleResponse = z.object({
  cycle: cycleSchema.nullable(),
  prompts: z.array(promptSchema),
  my_contributions: z.array(
    z.object({
      id: uuid,
      prompt_id: uuid,
      status: contributionStatus,
      text_body: z.string().nullable(),
      photos: z.array(photoSchema),
      submitted_at: z.string().nullable(),
    }),
  ),
  contribution_stats: z.object({
    submitted_count: z.number().int(),
    total_eligible_members: z.number().int(),
  }),
  editor: z.object({ user_id: uuid, name: z.string() }).nullable(),
  my_membership: z.object({ role: membershipRole, status: membershipStatus }),
});
export const setThemeRequest = z.object({ theme: z.string().min(1).max(LIMITS.themeMaxChars) });
export const setThemeResponse = z.object({ cycle: cycleSchema, prompts: z.array(promptSchema) });
export const themeSuggestionsResponse = z.object({
  suggestions: z.array(z.object({ title: z.string(), description: z.string() })),
  source: z.enum(['ai', 'fallback']),
});
export const createPromptRequest = z.object({
  text: z.string().min(1).max(LIMITS.promptMaxChars),
});
export const contributionStatsResponse = z.object({
  submitted_count: z.number().int(),
  total_eligible_members: z.number().int(),
});

// ---------- Contributions (API §2.7) ----------
export const createContributionRequest = z.object({
  prompt_id: uuid,
  family_id: uuid,
  text_body: z.string().max(LIMITS.contributionMaxChars).nullable().optional(),
});
export const updateContributionRequest = z.object({
  text_body: z.string().max(LIMITS.contributionMaxChars).nullable().optional(),
  status: contributionStatus.optional(),
});
export const myContributionsResponse = z.object({
  contributions: z.array(
    contributionSchema.extend({
      prompt_text: z.string(),
      family_name: z.string(),
      cycle_year_month: yearMonth,
      edition_id: uuid.nullable(),
    }),
  ),
  pagination: paginationSchema,
});

// ---------- Photos (API §2.8) ----------
export const presignRequest = z.object({
  contribution_id: uuid,
  mime_type: photoMimeType,
  size_bytes: z.number().int().positive().max(LIMITS.photoMaxBytes),
});
export const presignResponse = z.object({
  upload_url: z.string(),
  storage_key: z.string(),
  expires_in: z.number().int(),
});
export const confirmPhotoRequest = z.object({
  contribution_id: uuid,
  storage_key: z.string(),
  display_order: z.number().int().min(1).max(LIMITS.photosPerContribution),
});

// ---------- Editions (API §2.9) ----------
export const editionDetailResponse = z.object({
  id: uuid,
  cycle_id: uuid,
  family_id: uuid,
  family_name: z.string(),
  year_month: yearMonth,
  theme: z.string().nullable(),
  status: editionStatus,
  compiled_at: z.string().nullable(),
  pages: pagesJsonSchema,
  departments: z.array(
    z.object({ type: departmentType, status: departmentStatus, content_json: z.unknown() }),
  ),
  my_reactions: z.array(
    z.object({ id: uuid, target_type: reactionTargetType, target_id: z.string() }),
  ),
  reaction_counts: z.record(z.number().int()),
});
export const listEditionsQuery = z.object({
  family_id: uuid.optional(),
  year: z.coerce.number().int().optional(),
  participant_id: uuid.optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(50).default(10),
});
export const listEditionsResponse = z.object({
  editions: z.array(editionSummarySchema),
  pagination: paginationSchema,
});
export const createReactionRequest = z.object({
  target_type: reactionTargetType,
  target_id: z.string().min(1),
  type: reactionType.default('heart'),
});
export const createReactionResponse = z.object({ reaction: reactionSchema });

// ---------- Occasions (API §2.10) ----------
export const createOccasionRequest = z.object({
  subject_type: occasionSubjectType,
  subject_id: uuid,
  occasion_type: occasionType,
  title: z.string().min(1).max(120),
  date: isoDate,
  recurrence: occasionRecurrence.default('annual'),
});
export const updateOccasionRequest = createOccasionRequest.partial();
export const listOccasionsQuery = z.object({
  upcoming_only: z.coerce.boolean().optional(),
  month: yearMonth.optional(),
});
export const listOccasionsResponse = z.object({ occasions: z.array(occasionSchema) });

// ---------- Notifications (API §2.11) ----------
export const listNotificationsQuery = z.object({
  type: notificationType.optional(),
  is_read: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => (typeof v === 'string' ? v === 'true' : v))
    .optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export const listNotificationsResponse = z.object({
  notifications: z.array(notificationSchema),
  unread_count: z.number().int(),
  pagination: paginationSchema,
});
export const notificationPreferencesResponse = z.object({
  preferences: z.array(notificationPreferenceSchema),
});
export const updateNotificationPreferencesRequest = z.object({
  preferences: z.array(z.object({ type: notificationType, enabled: z.boolean() })),
});

// ---------- Devices / push (architecture §3.7) ----------
export const registerDeviceRequest = z.object({
  platform: devicePlatform.default('web'),
  push_subscription: z.object({
    endpoint: z.string().url(),
    expirationTime: z.number().nullable().optional(),
    keys: z.object({ p256dh: z.string(), auth: z.string() }),
  }),
});
export const registerDeviceResponse = z.object({ device_id: uuid });
/** The VAPID public key comes from the API so the browser always subscribes with the
 * same key the Worker signs with (no build-time copy to drift). */
export const pushConfigResponse = z.object({ vapid_public_key: z.string().nullable() });
export const testPushResponse = z.object({
  devices: z.number().int(),
  delivered: z.number().int(),
});

// ---------- Editor handoff (API §2.12) ----------
export const editorCandidatesResponse = z.object({
  current_editor: z.object({ user_id: uuid, name: z.string() }).nullable(),
  candidates: z.array(
    z.object({
      user_id: uuid,
      name: z.string(),
      avatar_url: z.string().nullable(),
      last_edited_year_month: yearMonth.nullable(),
    }),
  ),
});
export const editorHandoffRequest = z.object({ next_editor_user_id: uuid });
export const editorHandoffResponse = z.object({ next_cycle: cycleSchema });

// ---------- Admin (API §2.13) ----------
export const emergencyEditorRequest = z.object({ new_editor_user_id: uuid });

// ---------- Home dashboard (App Flow §2) ----------
export const homeResponse = z.object({
  families: z.array(
    familySummarySchema.extend({
      cycle: cycleSchema.nullable(),
      editor_name: z.string().nullable(),
      i_am_editor: z.boolean(),
      unanswered_prompt_count: z.number().int(),
      submitted_count: z.number().int(),
    }),
  ),
  latest_edition: editionSummarySchema.nullable(),
  unread_notifications: z.number().int(),
});

/** Every "message ok" response uses this shape. */
export const messageResponse = z.object({ message: z.string() });

export type CreateFamilyRequest = z.infer<typeof createFamilyRequest>;
export type CurrentCycleResponse = z.infer<typeof currentCycleResponse>;
export type EditionDetailResponse = z.infer<typeof editionDetailResponse>;
export type FamilyDetailResponse = z.infer<typeof familyDetailResponse>;
export type HomeResponse = z.infer<typeof homeResponse>;
export type ListEditionsResponse = z.infer<typeof listEditionsResponse>;
export type ListNotificationsResponse = z.infer<typeof listNotificationsResponse>;
export type MyContributionsResponse = z.infer<typeof myContributionsResponse>;
export type ThemeSuggestionsResponse = z.infer<typeof themeSuggestionsResponse>;
export type EditorCandidatesResponse = z.infer<typeof editorCandidatesResponse>;
export type CycleStatusValue = z.infer<typeof cycleStatus>;
